<script lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { ArrayOrNested } from '#ui/types/utils';
import type { SidebarContentProps } from './Content/Content.vue';
import type { SidebarContentItemEmits, SidebarItem } from './Content/ContentItem.vue';
import type { SidebarAction } from './Header/HeaderActions.vue';
import { tv } from 'tailwind-variants';
import { isNullishOrEmpty } from '#layers/base/public';
import DEmpty from '#layers/ui/components/Empty.vue';

const sidebar = tv({
	slots: {
		root: 'flex h-full flex-col border-bs border-transparent', // transparent border ensures it matches spacebar
		header: 'flex h-(--secondary-header-height) items-center justify-between gap-1 ps-5 pe-3',
		headerTitle: 'truncate text-sm font-semibold text-toned',
	},
});

export interface SidebarProps<T extends ArrayOrNested<SidebarItem> = ArrayOrNested<SidebarItem>> {
	items?: SidebarContentProps<T>['items'];
	title?: string;
	collapsed?: boolean;
	headerActions?: SidebarAction[];
	helpLink?: RouteLocationRaw;
	isEditMode?: boolean;
	empty?: InstanceType<typeof DEmpty>['$props'] & Partial<InstanceType<typeof DEmpty>['$emit']>;
	class?: string;
	ui?: ThemeSlots<typeof sidebar>;
}

export interface SidebarEmits extends SidebarContentItemEmits {
	hide: [id: SidebarItem['id']];
	show: [id: SidebarItem['id']];
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SidebarItem>">
import { computed, useSlots } from 'vue';

const props = defineProps<SidebarProps<T>>();
const emit = defineEmits<SidebarEmits>();

const theme = computed(() => sidebar());

const slots = useSlots();
const hasHeaderActions = computed(() => !!slots['header-actions'] || !isNullishOrEmpty(props.headerActions));

function handleHide(id: SidebarItem['id']) {
	emit('hide', id);
}

function handleShow(id: SidebarItem['id']) {
	emit('show', id);
}
</script>

<template>
	<aside :class="theme.root({ class: [ui?.root, props.class, !hasHeaderActions && 'pbs-2'] })" :aria-label="title">
		<div v-if="hasHeaderActions && !collapsed" :class="theme.header({ class: ui?.header })">
			<span v-if="title" :class="theme.headerTitle({ class: ui?.headerTitle })">
				<slot name="header-title">
					{{ title }}
				</slot>
			</span>

			<DSidebarHeaderActions :actions="headerActions">
				<slot name="header-actions" />
			</DSidebarHeaderActions>
		</div>

		<slot name="content-before" />

		<div
			class="flex-1 overflow-y-auto px-3"
			:class="[collapsed ? `space-y-1` : `space-y-4`]"
		>
			<slot name="content-top" />

			<DSidebarContent
				v-if="!isNullishOrEmpty(items)"
				:items
				:collapsed
				:edit-mode="isEditMode"
				@hide="handleHide"
				@show="handleShow"
			/>
			<div v-else-if="empty" class="rounded-md border border-dashed">
				<DEmpty
					v-bind="empty"
					:ui="{
						title: 'text-base',
						root: 'gap-4',
					}"
				/>
			</div>

			<slot name="content-bottom" />
		</div>
		<DSidebarFooter v-if="!collapsed" class="mt-auto" :help-link>
			<slot name="footer" />
		</DSidebarFooter>
	</aside>
</template>
